import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  getSettings,
  updateSettings,
  GlobalSettings,
} from '../../services/adminApi';

/**
 * Admin Settings Page
 *
 * Manage global wholesale settings:
 * - Wholesale discount percentage
 * - Quantity discount threshold
 * - Quantity discount amount
 * - Minimum order value
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    wholesaleDiscountPercent: 50,
    quantityDiscountThreshold: 3,
    quantityDiscountAmount: 2.5,
    minimumOrderValue: 300,
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSettings();
      setSettings(response.settings);
      setFormData({
        wholesaleDiscountPercent: response.settings.wholesaleDiscountPercent,
        quantityDiscountThreshold: response.settings.quantityDiscountThreshold,
        quantityDiscountAmount: response.settings.quantityDiscountAmount,
        minimumOrderValue: response.settings.minimumOrderValue,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await updateSettings(formData);
      setSuccess('Settings saved successfully');

      // Refresh settings to get updated timestamp
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setFormData((prev) => ({ ...prev, [field]: numValue }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-lg font-display text-cream tracking-wide">
          Global Settings
        </h2>
        <p className="text-sm text-text-muted mt-1">
          Configure default pricing rules for all wholesale products
        </p>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className="max-w-2xl">
        <div className="card space-y-6">
          {/* Wholesale Discount */}
          <div>
            <label className="block text-sm font-medium text-cream mb-2">
              Wholesale Discount (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={formData.wholesaleDiscountPercent}
              onChange={(e) =>
                handleInputChange('wholesaleDiscountPercent', e.target.value)
              }
              className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
            <p className="text-xs text-text-muted mt-1">
              Percentage off retail price for all wholesale customers
            </p>
          </div>

          {/* Quantity Discount Threshold */}
          <div>
            <label className="block text-sm font-medium text-cream mb-2">
              Quantity Discount Threshold
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={formData.quantityDiscountThreshold}
              onChange={(e) =>
                handleInputChange('quantityDiscountThreshold', e.target.value)
              }
              className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
            <p className="text-xs text-text-muted mt-1">
              Minimum quantity per item to qualify for additional discount
            </p>
          </div>

          {/* Quantity Discount Amount */}
          <div>
            <label className="block text-sm font-medium text-cream mb-2">
              Quantity Discount Amount ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.quantityDiscountAmount}
              onChange={(e) =>
                handleInputChange('quantityDiscountAmount', e.target.value)
              }
              className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
            <p className="text-xs text-text-muted mt-1">
              Additional discount per unit when quantity threshold is met
            </p>
          </div>

          {/* Minimum Order Value */}
          <div>
            <label className="block text-sm font-medium text-cream mb-2">
              Minimum Order Value ($)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.minimumOrderValue}
              onChange={(e) =>
                handleInputChange('minimumOrderValue', e.target.value)
              }
              className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
            <p className="text-xs text-text-muted mt-1">
              Minimum cart total required to place an order
            </p>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="p-3 bg-red-950/50 border border-red-800/30 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-950/50 border border-green-800/30 rounded-lg text-green-300 text-sm">
              {success}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
            <div className="text-xs text-text-muted">
              {settings?.updatedAt && (
                <>
                  Last updated:{' '}
                  {new Date(settings.updatedAt).toLocaleString()}
                  {settings.updatedBy && ` by ${settings.updatedBy}`}
                </>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-accent text-workshop rounded-lg hover:bg-accent-light disabled:opacity-50 transition-colors uppercase tracking-wide text-sm font-medium"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </form>

      {/* Settings Preview */}
      <div className="max-w-2xl">
        <h3 className="text-md font-display text-cream tracking-wide mb-4">
          Pricing Preview
        </h3>
        <div className="card bg-white/5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-muted">Example retail price:</span>
              <span className="ml-2 font-mono">$100.00</span>
            </div>
            <div>
              <span className="text-text-muted">Wholesale price:</span>
              <span className="ml-2 font-mono text-green-400">
                ${(100 * (1 - formData.wholesaleDiscountPercent / 100)).toFixed(2)}
              </span>
            </div>
            <div className="col-span-2 pt-2 border-t border-white/[0.06]">
              <span className="text-text-muted">
                With {formData.quantityDiscountThreshold}+ units:
              </span>
              <span className="ml-2 font-mono text-green-400">
                ${(
                  100 * (1 - formData.wholesaleDiscountPercent / 100) -
                  formData.quantityDiscountAmount
                ).toFixed(2)}
                /unit
              </span>
              <span className="ml-2 text-xs text-text-muted">
                (saves ${formData.quantityDiscountAmount.toFixed(2)} per unit)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
